import { ReactNode } from 'react';
import { FlaskConical } from 'lucide-react';
import {
  Beaker,
  Dropper,
  LitmusPaper,
  StirringRod,
  SoilContainer,
  Timer,
  TestTube,
  BunsenBurner,
  TestTubeRack,
  Burette,
  Spectrophotometer,
  pHMeter,
  AnalyticalBalance,
  Crucible,
  Centrifuge,
} from '@/components/lab';

export interface LabBenchProps {
  experimentName: string;
  completedCount: number;
  totalSteps: number;
  equipment: string[];
  labMode: 'guided' | 'free';
  currentStep: number;
  children?: ReactNode;
  settlingProgress?: number;
  onEquipmentClick?: (equipmentId: string) => void;
  equipmentState?: Record<string, unknown>;
}

// Equipment component mapping
const equipmentComponents: Record<string, React.ComponentType<unknown>> = {
  beaker: Beaker,
  dropper: Dropper,
  litmusPaper: LitmusPaper,
  stirringRod: StirringRod,
  soilContainer: SoilContainer,
  timer: Timer,
  testTube: TestTube,
  bunsenBurner: BunsenBurner,
  testTubeRack: TestTubeRack,
  burette: Burette,
  spectrophotometer: Spectrophotometer,
  phMeter: pHMeter,
  analyticalBalance: AnalyticalBalance,
  crucible: Crucible,
  centrifuge: Centrifuge,
};

export default function LabBench({
  experimentName,
  completedCount,
  totalSteps,
  children,
  settlingProgress,
}: LabBenchProps) {
  return (
    <div className="lab-bench">
      <div className="bench-header">
        <div className="bench-title">
          <FlaskConical size={16} />
          <h3>Lab Bench - {experimentName}</h3>
        </div>
        <span className="bench-progress">
          {completedCount}/{totalSteps} Complete
        </span>
      </div>

      <div className="bench-area">
        {/* Progress bar for settling/processing */}
        {settlingProgress !== undefined && settlingProgress > 0 && (
          <div className="settling-progress">
            <div
              className="settling-bar"
              style={{ width: `${settlingProgress * 100}%` }}
            />
            <span>{Math.round(settlingProgress * 100)}%</span>
          </div>
        )}

        {/* Equipment rendering area - children passed from experiment component */}
        <div className="equipment-area">
          {children}
        </div>

        {/* Bench surface */}
        <div className="bench-surface" />
      </div>

      <style>{`
        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: visible;
          min-height: fit-content;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
          flex-shrink: 0;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
          margin: 0;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          min-height: fit-content;
        }

        .settling-progress {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 24px;
          background: var(--border);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .settling-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          background: var(--secondary);
          transition: width 0.3s;
        }

        .settling-progress span {
          position: relative;
          font-size: 12px;
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }

        .equipment-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          width: 100%;
          padding-bottom: 20px;
        }

        .equipment-grid {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
        }

        .beakers-section {
          display: flex;
          gap: 60px;
          align-items: flex-end;
        }

        .tools-section {
          display: flex;
          gap: 24px;
          align-items: flex-end;
          flex-wrap: wrap;
          justify-content: center;
        }

        .bench-surface {
          display: none; /* Hiding decorative bench surface - was causing layout issues */
        }
      `}</style>
    </div>
  );
}

// Export equipment components for convenience
export { equipmentComponents };
